import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import ServiceInstanceTemplateManager from '@/components/tree/service-instance-template-manager'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { resolveSignupTemplateEditorContext } from '@/lib/service-instance-templates'
import { orgTreeApi } from '@/services/org-tree'

export default function OrgTemplatesPage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const instanceQuery = useQuery({
    queryKey: ['org-templates-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const templateEditorContext = useMemo(
    () =>
      resolveSignupTemplateEditorContext(
        instanceQuery.data?.service_variant ?? instanceQuery.data?.service_data?.variant_unique,
        instanceQuery.data?.service_variant_name ?? instanceQuery.data?.type,
      ),
    [instanceQuery.data],
  )

  if (!serviceInstanceId) {
    return (
      <div className="rounded-[28px] border border-destructive/30 bg-card p-8 text-center">
        <p className="text-sm font-medium text-destructive">Missing service context</p>
        <p className="mt-2 text-sm text-muted-foreground">Select a role with an organisation or branch service instance again.</p>
      </div>
    )
  }

  if (instanceQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-28 w-full rounded-[28px]" />
        <Skeleton className="h-96 w-full rounded-[28px]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-2">
      <Card className="rounded-[28px] border-border/80">
        <CardHeader>
          <CardTitle>Templates</CardTitle>
          <CardDescription>
            Signup emails, SMS, and push templates for this organisation service instance. Each template has a stable key so UI and backend stay aligned.
          </CardDescription>
        </CardHeader>
      </Card>
      <ServiceInstanceTemplateManager
        serviceInstanceId={serviceInstanceId}
        currentConfig={instanceQuery.data?.config ?? null}
        entityLabelLower="service instance"
        templateEditorContext={templateEditorContext}
      />
    </div>
  )
}
